/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {usePDPSearchParams} from './use-pdp-search-params'
import {useMemo} from 'react'

/**
 * This hook will return only the params that are also product attributes for the
 * passed in product object.
 */
export const useVariationParams = (
    product,
    isProductPartOfSet = false,
    isProductPartOfBundle = false
) => {
    const {variationAttributes = [], variationValues = {}} = product || {}

    const [allParams, productParams] = usePDPSearchParams(
        isProductPartOfSet ? product?.setProductId : product?.id
    )
    const params = isProductPartOfSet || isProductPartOfBundle ? productParams : allParams

    // Using all the variation attribute id from the array generated below, get
    // the value if there is one from the location search params and add it to the
    // accumulator.
    const variationParams = useMemo(
        () =>
            variationAttributes.reduce((acc, {id: key, values}) => {
                const urlValue =
                    params.get(`${key}`) ||
                    // handle SiteGenesis parameter
                    params.get(`dwvar_${product?.id}_${key}`)

                // if url value exists - use it
                if (urlValue && values?.find(({value}) => value === urlValue)) {
                    return {...acc, [key]: urlValue}
                }

                // otherwise check if there is a value in the variationValues object
                const value = variationValues?.[key]
                return value ? {...acc, [key]: value} : acc
            }, {}),
        [variationAttributes, params, product?.id, variationValues]
    )

    return variationParams
}
